/**
 * AudioManager — singleton that generates all game sounds procedurally
 * using the Web Audio API. No audio files are needed.
 *
 * Usage:
 *   import { audioManager } from '../audio/AudioManager';
 *   audioManager.play('shoot');
 *   audioManager.toggleMute();
 */

const STORAGE_KEY = 'henry_muted';

export type SoundName =
  | 'shoot'
  | 'enemyHit'
  | 'enemyDeath'
  | 'playerDamage'
  | 'levelUp'
  | 'bossEntrance'
  | 'coinPickup'
  | 'gameOver'
  | 'buttonClick';

class AudioManager {
  private ctx: AudioContext | null = null;
  private _muted: boolean;

  constructor() {
    try {
      this._muted = localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      this._muted = false;
    }
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  get isMuted(): boolean {
    return this._muted;
  }

  /** Toggle mute state, persist to localStorage, return new mute state. */
  toggleMute(): boolean {
    this._muted = !this._muted;
    try {
      localStorage.setItem(STORAGE_KEY, String(this._muted));
    } catch {
      // Ignore storage errors (private browsing, quota exceeded, etc.)
    }
    return this._muted;
  }

  /**
   * Resume AudioContext if it was suspended (browsers require a user gesture
   * before audio can play). Call this once on the first user interaction.
   */
  resume(): void {
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
  }

  /** Play a named sound effect. Does nothing when muted. */
  play(sound: SoundName): void {
    if (this._muted) return;
    try {
      const ctx = this.getCtx();
      switch (sound) {
        case 'shoot':        this.playShoot(ctx);        break;
        case 'enemyHit':     this.playEnemyHit(ctx);     break;
        case 'enemyDeath':   this.playEnemyDeath(ctx);   break;
        case 'playerDamage': this.playPlayerDamage(ctx); break;
        case 'levelUp':      this.playLevelUp(ctx);      break;
        case 'bossEntrance': this.playBossEntrance(ctx); break;
        case 'coinPickup':   this.playCoinPickup(ctx);   break;
        case 'gameOver':     this.playGameOver(ctx);     break;
        case 'buttonClick':  this.playButtonClick(ctx);  break;
      }
    } catch {
      // Silently ignore audio errors (e.g. browser policy blocks)
    }
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  private getCtx(): AudioContext {
    if (!this.ctx) {
      this.ctx = new AudioContext();
    }
    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
    return this.ctx;
  }

  /** Shoot — short high-freq blip */
  private playShoot(ctx: AudioContext): void {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'square';
    osc.frequency.setValueAtTime(900, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(1400, ctx.currentTime + 0.04);
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.07);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.07);
  }

  /** Enemy hit — short noise burst */
  private playEnemyHit(ctx: AudioContext): void {
    const bufferSize = ctx.sampleRate * 0.05;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
    }
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.05);
    source.connect(gain);
    gain.connect(ctx.destination);
    source.start(ctx.currentTime);
  }

  /** Enemy death — descending tone */
  private playEnemyDeath(ctx: AudioContext): void {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(440, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(80, ctx.currentTime + 0.18);
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.18);
  }

  /** Player damage — low buzz */
  private playPlayerDamage(ctx: AudioContext): void {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(120, ctx.currentTime);
    osc.frequency.setValueAtTime(80, ctx.currentTime + 0.05);
    gain.gain.setValueAtTime(0.4, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.2);
  }

  /** Level up — ascending arpeggio */
  private playLevelUp(ctx: AudioContext): void {
    const notes = [523.25, 659.25, 783.99, 1046.5]; // C5, E5, G5, C6
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      const t = ctx.currentTime + i * 0.1;
      osc.frequency.setValueAtTime(freq, t);
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.35, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
      osc.start(t);
      osc.stop(t + 0.18);
    });
  }

  /** Boss entrance — deep rumble */
  private playBossEntrance(ctx: AudioContext): void {
    // Sub-bass oscillator
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(55, ctx.currentTime);
    osc.frequency.linearRampToValueAtTime(40, ctx.currentTime + 0.6);
    gain.gain.setValueAtTime(0.5, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.7);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.7);

    // Noise layer
    const bufferSize = ctx.sampleRate * 0.5;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize) * 0.5;
    }
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0.2, ctx.currentTime);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    noise.connect(noiseGain);
    noiseGain.connect(ctx.destination);
    noise.start(ctx.currentTime);
  }

  /** Coin pickup — pleasant ding */
  private playCoinPickup(ctx: AudioContext): void {
    const freqs = [1318.5, 1760]; // E6, A6
    freqs.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      const t = ctx.currentTime + i * 0.07;
      osc.frequency.setValueAtTime(freq, t);
      gain.gain.setValueAtTime(0.3, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
      osc.start(t);
      osc.stop(t + 0.25);
    });
  }

  /** Game over — descending sad tones */
  private playGameOver(ctx: AudioContext): void {
    const notes = [440, 369.99, 311.13, 261.63]; // A4, F#4, Eb4, C4
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      const t = ctx.currentTime + i * 0.22;
      osc.frequency.setValueAtTime(freq, t);
      gain.gain.setValueAtTime(0.35, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
      osc.start(t);
      osc.stop(t + 0.35);
    });
  }

  /** Button click — soft tick */
  private playButtonClick(ctx: AudioContext): void {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(600, ctx.currentTime);
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.04);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.04);
  }
}

/** Singleton instance — import this everywhere audio is needed. */
export const audioManager = new AudioManager();
